// Single source of truth for the app's icon library.
//
// Each icon is DATA, not a component: a list of "parts" rendered inside one host <div>.
// A part is either a bare FontAwesome layer or a `{ stack: [...] }` group (fa-stack).
// The <app-icon name="…"> component (icon.component.ts) renders this; the theme-editor
// "icons" tab lists it. Adding an icon = one entry here — no new component, no imports.

import { IconPrefix } from '@fortawesome/fontawesome-svg-core';

export type FaRef = [IconPrefix, string];

export interface FaLayer {
    icon: FaRef;
    transform?: string;
    flip?: 'horizontal' | 'vertical' | 'both';
    rotate?: number;
    size?: string;          // e.g. 'lg', '4x' — fa-icon SizeProp
    stackItemSize?: '1x' | '2x';
    style?: string;         // raw inline style (used for the yellow badge circles)
}

export interface FaStackPart {
    stack: FaLayer[];
}

export type IconPart = FaLayer | FaStackPart;
export type IconDef = IconPart[];

// Shared style for the yellow "badge" circle behind role/status glyphs.
const BADGE = 'color: rgba(255, 216, 0, 0.7)';

export const ICONS = {
    // — navigation / paging —
    'nav-first': [{ icon: ['fas', 'step-backward'] }],
    'nav-previous': [{ icon: ['fas', 'caret-left'], transform: 'grow-8 left-2' }],
    'nav-next': [{ icon: ['fas', 'caret-right'], transform: 'grow-8 right-2' }],
    'nav-last': [{ icon: ['fas', 'step-forward'] }],
    'caret-left': [{ icon: ['fas', 'caret-left'] }],
    'caret-right': [{ icon: ['fas', 'caret-right'] }],
    'caret-down': [{ icon: ['fas', 'caret-down'] }],
    'arrow-left': [{ icon: ['fas', 'arrow-left'] }],
    'arrow-right': [{ icon: ['fas', 'arrow-right'] }],
    'arrow-dash-left': [{ stack: [
        { icon: ['fas', 'chevron-left'], stackItemSize: '1x', transform: 'grow-4 left-6' },
        { icon: ['fas', 'minus'], stackItemSize: '1x', transform: 'right-4' },
    ] }],
    'arrow-dash-right': [{ stack: [
        { icon: ['fas', 'minus'], stackItemSize: '1x', transform: 'left-4' },
        { icon: ['fas', 'chevron-right'], stackItemSize: '1x', transform: 'grow-4 right-6' },
    ] }],
    'arrow-dot-left': [{ stack: [
        { icon: ['fas', 'circle'], stackItemSize: '1x', transform: 'shrink-12 left-6' },
        { icon: ['fas', 'arrow-left'], stackItemSize: '1x', transform: 'right-6' },
    ] }],
    'arrow-dot-right': [{ stack: [
        { icon: ['fas', 'arrow-right'], stackItemSize: '1x', transform: 'left-6' },
        { icon: ['fas', 'circle'], stackItemSize: '1x', transform: 'shrink-12 right-6' },
    ] }],

    // — filter —
    'filter': [{ icon: ['fas', 'filter']}],
    'remove-filter': [{ stack: [
        { icon: ['fas', 'filter'], transform: 'right-5' },
        { icon: ['fas', 'xmark'], transform: 'shrink-7 down-5 left-6' },
    ] }],
    'filter-edit': [{ stack: [
        { icon: ['fas', 'filter'], transform: 'grow-2 right-4' },
        { icon: ['fas', 'pencil-alt'], transform: 'shrink-7 down-5 left-6' },
    ] }],
    'sort': [{ icon: ['fas', 'sort'] }],
    'sort-asc': [{ icon: ['fas', 'sort-up'] }],
    'sort-desc': [{ icon: ['fas', 'sort-down'] }],

    // — generic actions / glyphs —
    'gear': [{ icon: ['fas', 'gear'] }],
    'bars': [{ icon: ['fas', 'bars'] }],
    'camera': [{ icon: ['fas', 'camera'] }],
    'camera-plus': [{ stack: [
        { icon: ['fas', 'camera'], stackItemSize: '1x', transform: 'left-5' },
        { icon: ['fas', 'plus'], stackItemSize: '1x', transform: 'right-9 shrink-9 up-5 left-1' },
    ] }],
    'download': [{ icon: ['fas', 'download'] }],
    'edit': [{ icon: ['fas', 'edit'] }],
    // 'edit-page': [{ icon: ['fas', 'edit'], transform: 'grow-8' }],
    'ellipsis-v': [{ icon: ['fas', 'ellipsis-v'] }],
    'ellipsis-h': [{ icon: ['fas', 'ellipsis-h'] }],
    'file': [{ icon: ['fas', 'file-alt'] }],
    'copy': [{ icon: ['far', 'copy'] }],
    'id-card': [{ icon: ['fas', 'id-card'] }],
    'image': [{ icon: ['fas', 'image'] }],
    // De pen ligt deels óver de image. Een iets grotere pen-kopie in de achtergrond-
    // kleur eronder vormt een "rand" die de pen uit de image lijkt te snijden (knockout-
    // effect). De randkleur komt uit --icon-knockout-bg (fallback transparent → geen
    // rand), zodat het icoon generiek blijft: zet die var op de achtergrond waar je het
    // icoon plaatst, bv. op de action-knop `--icon-knockout-bg: var(--color-button-action-bg)`.
    'image-edit': [{ stack: [
        { icon: ['fas', 'image'], stackItemSize: '1x', transform: 'left-5' },
        { icon: ['fas', 'pencil-alt'], stackItemSize: '1x', transform: 'shrink-2 right-2 down-1', style: 'color: var(--icon-knockout-bg, white)' },
        { icon: ['fas', 'pencil-alt'], stackItemSize: '1x', transform: 'shrink-5 right-2 down-1' },
    ] }],
    'image-favourite': [{ stack: [
        { icon: ['fas', 'image'], stackItemSize: '1x', transform: 'left-5' },
        { icon: ['fas', 'star'], stackItemSize: '1x', transform: 'shrink-10 right-8 up-5' },
    ] }],
    'images': [{ icon: ['fas', 'images'] }],
    'images-plus': [{ stack: [
        { icon: ['fas', 'images'], stackItemSize: '1x', transform: 'left-5' },
        { icon: ['fas', 'plus'], stackItemSize: '1x', transform: 'right-9 shrink-9 up-5 left-1' },
    ] }],
    'list': [{ icon: ['fas', 'list'] }],
    'list-tree': [{ icon: ['fas', 'folder-tree'] }],
    'lock': [{ icon: ['fas', 'lock'] }],
    'lock-small': [{ icon: ['fas', 'lock'], transform: 'shrink-6' }],
    'unlock': [{ icon: ['fas', 'lock-open'] }],
    // Lock-status als rol-achtig icoon (gele cirkel zoals de rol-iconen), bv. vóór de tekst in een lijstrij.
    'lock-badge': [{ stack: [
        { icon: ['fas', 'circle'], stackItemSize: '1x', style: BADGE },
        { icon: ['fas', 'lock'], stackItemSize: '1x', transform: 'shrink-7', style: 'color: rgba(186, 31, 28)' },
    ] }],
    'minus': [{ icon: ['fas', 'minus'] }],
    'plus': [{ icon: ['fas', 'plus'] }],
    'plus-pen': [
        { icon: ['fas', 'plus'] },
        { icon: ['fas', 'pencil-alt'], transform: 'left-2 shrink-6 up-2' },
    ],
    'plus-save': [
        { icon: ['fas', 'plus'] },
        { icon: ['fas', 'save'], transform: 'left-2 shrink-6 up-2' },
    ],
    'deleted-list': [
        { icon: ['fas', 'list'] },
        { icon: ['fas', 'xmark'], transform: 'right-2 shrink-3 up-4' },
    ],
    'plus-list': [
        { icon: ['fas', 'list'] },
        { icon: ['fas', 'plus'], transform: 'shrink-4 up-4' },
    ],
    'minus-list': [
        { icon: ['fas', 'list'] },
        { icon: ['fas', 'minus'], transform: 'shrink-4 up-4' },
    ],
    'power-off': [{ icon: ['fas', 'power-off'] }],
    'report-chart': [{ icon: ['fas', 'chart-line'] }],
    'rotate': [{ icon: ['fas', 'redo'] }],
    'save': [{ icon: ['fas', 'save'] }],
    'send-plane': [{ icon: ['fas', 'paper-plane'] }],
    'share': [{ icon: ['fas', 'share-nodes'] }],
    'send-mail': [
        { icon: ['fas', 'align-right'], transform: 'shrink-4' },
        { icon: ['fas', 'envelope'] },
    ],
    'synchronise': [{ icon: ['fas', 'sync'] }],
    'xmark': [{ icon: ['fas', 'xmark'] }],
    'check': [{ icon: ['fas', 'check'] }],
    'check-double': [{ icon: ['fas', 'check-double'] }],
    'circle-exclamation': [{ icon: ['fas', 'circle-exclamation'] }],
    'trash': [{ icon: ['fas', 'trash'] }],
    'square': [{ icon: ['fas', 'square'] }],

    // — people / roles (badge = yellow circle + glyph) —
    'administrator': [{ stack: [
        { icon: ['fas', 'circle'], stackItemSize: '1x', style: BADGE },
        { icon: ['fas', 'user-plus'], stackItemSize: '1x', transform: 'shrink-8 up-.5' },
    ] }],
    'domain-owner': [{ stack: [
        { icon: ['fas', 'circle'], stackItemSize: '1x', style: BADGE },
        { icon: ['fas', 'crown'], stackItemSize: '1x', transform: 'shrink-8 up-.5' },
    ] }],
    'project-manager': [{ stack: [
        { icon: ['fas', 'circle'], stackItemSize: '1x', style: BADGE },
        { icon: ['fas', 'user-tie'], stackItemSize: '1x', transform: 'shrink-8 up-.5' },
    ] }],
    'set-control': [
        { stack: [
            { icon: ['fas', 'circle'], stackItemSize: '1x', transform: 'right-5', style: BADGE },
            { icon: ['fas', 'user-plus'], stackItemSize: '1x', transform: 'shrink-8 right-5 up-.5' },
        ] },
        { icon: ['fas', 'plus'], transform: 'left-6 shrink-6 up-2 left-5' },
    ],
    'no-control': [
        { stack: [
            { icon: ['fas', 'circle'], stackItemSize: '1x', transform: 'right-5', style: BADGE },
            { icon: ['fas', 'user'], stackItemSize: '1x', transform: 'shrink-8 right-5 up-.5' },
        ] },
        { icon: ['fas', 'xmark'], transform: 'left-6 shrink-6 up-2 left-5' },
    ],
    'user': [{ stack: [
        { icon: ['fas', 'circle'], stackItemSize: '1x', style: BADGE },
        { icon: ['fas', 'user'], stackItemSize: '1x', transform: 'shrink-8 up-.5' },
    ] }],
    'users': [{ icon: ['fas', 'users'], transform: 'right-2' }],
    'supplier': [{ icon: ['fas', 'truck'] }],
    'employee': [{ icon: ['fas', 'users'] }],
    'customer': [{ icon: ['fas', 'glass-cheers'] }],
    'contractor': [{ icon: ['fas', 'address-card'] }],
    'supplier-badge': [{ stack: [
        { icon: ['fas', 'circle'], stackItemSize: '1x', style: BADGE },
        { icon: ['fas', 'truck'], stackItemSize: '1x', transform: 'shrink-8 up-.5' },
    ] }],
    'employee-badge': [{ stack: [
        { icon: ['fas', 'circle'], stackItemSize: '1x', style: BADGE },
        { icon: ['fas', 'users'], stackItemSize: '1x', transform: 'shrink-8 up-.5' },
    ] }],
    'customer-badge': [{ stack: [
        { icon: ['fas', 'circle'], stackItemSize: '1x', style: BADGE },
        { icon: ['fas', 'glass-cheers'], stackItemSize: '1x', transform: 'shrink-8 up-.5' },
    ] }],
    'contractor-badge': [{ stack: [
        { icon: ['fas', 'circle'], stackItemSize: '1x', style: BADGE },
        { icon: ['fas', 'address-card'], stackItemSize: '1x', transform: 'shrink-8 up-.5' },
    ] }],

    // — tab dock controls (two minimize bars forming a frame + a directional glyph) —
    'tab-max': [{ stack: [
        { icon: ['fas', 'window-minimize'], stackItemSize: '1x', transform: 'grow-4 up-2', flip: 'vertical' },
        { icon: ['fas', 'window-minimize'], stackItemSize: '1x', transform: 'grow-4 up-2 left-1', rotate: 90 },
        { icon: ['fas', 'caret-up'], stackItemSize: '1x', transform: 'grow-2 up-2 right-1' },
        { icon: ['fas', 'caret-up'], stackItemSize: '1x', transform: 'grow-2 down-4 right-1' },
    ] }],
    'tab-up': [{ stack: [
        { icon: ['fas', 'window-minimize'], stackItemSize: '1x', transform: 'grow-4 up-3', flip: 'vertical' },
        { icon: ['fas', 'window-minimize'], stackItemSize: '1x', transform: 'grow-4 up-2 left-1', rotate: 90 },
        { icon: ['fas', 'expand-alt'], stackItemSize: '1x', transform: 'right-4', rotate: 90 },
    ] }],
    'tab-down': [{ stack: [
        { icon: ['fas', 'window-minimize'], stackItemSize: '1x', transform: 'grow-4 up-3', flip: 'vertical' },
        { icon: ['fas', 'window-minimize'], stackItemSize: '1x', transform: 'grow-4 up-2 left-1', rotate: 90 },
        { icon: ['fas', 'caret-down'], stackItemSize: '1x', transform: 'right-1', rotate: 90 },
    ] }],
    'tab-min': [{ stack: [
        { icon: ['fas', 'window-minimize'], stackItemSize: '1x', transform: 'grow-4 up-2', flip: 'vertical' },
        { icon: ['fas', 'window-minimize'], stackItemSize: '1x', transform: 'grow-4 up-1 left-1', rotate: 90 },
        { icon: ['fas', 'compress-alt'], stackItemSize: '1x', transform: 'right-4', rotate: 90 },
    ] }],
    'tab-reset': [{ stack: [
        { icon: ['fas', 'window-minimize'], stackItemSize: '1x', transform: 'grow-4 up-2', flip: 'vertical' },
        { icon: ['fas', 'window-minimize'], stackItemSize: '1x', transform: 'grow-4 up-1 left-1', rotate: 90 },
        { icon: ['fas', 'rotate-left'], stackItemSize: '1x', transform: 'shrink-4 down-2 right-1' },
    ] }],
} satisfies Record<string, IconDef>;

export type IconName = keyof typeof ICONS;

export const ICON_NAMES = Object.keys(ICONS) as IconName[];

export function isStackPart(part: IconPart): part is FaStackPart {
    return (part as FaStackPart).stack !== undefined;
}
